import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KenoBetType } from '@nexbit/types'
import { LocaleContext, saveLang, savedLang, translator } from '@/i18n'
import { api, session, type BetLimit, type TableState } from '@/lib/api'
import { BoardContext, type Board, type DisplayMode } from '@/lib/board'
import { GameLot } from '@/components/GameLot'
import { Header } from '@/components/Header'
import Sortable from 'sortablejs'

const CHECK_UPDATE_MS = 10_000
const CHECK_BALANCE_MS = 30_000
const MIN_REFRESH_GAP_MS = 800

const winAudio = typeof Audio !== 'undefined' ? new Audio('/assets/sound/win.mp3') : null
const lossAudio = typeof Audio !== 'undefined' ? new Audio('/assets/sound/loss.mpeg') : null

/** The KENO board: four table cards under the reference header. */
export function BoardPage({ limit: tier, onDisconnected }: { limit: BetLimit; onDisconnected: () => void }) {
  const [langId, setLangId] = useState(savedLang)
  const t = useMemo(() => translator(langId), [langId])

  const [tables, setTables] = useState<TableState[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const serverOffset = useRef(0)
  const [nowMs, setNowMs] = useState(Date.now())

  const [username, setUsername] = useState('')
  const [balance, setBalance] = useState<number | null>(null)
  const [checking, setChecking] = useState(false)
  const [lastStake, setLastStake] = useState<number | ''>('')
  const [stakes, setStakes] = useState<Board['stakes']>({})

  const [displayMode, setDisplayMode] = useState<DisplayMode>('normal')
  const [soundOn, setSoundOn] = useState(true)
  const [fullscreenId, setFullscreenId] = useState<string | null>(null)

  // ── Table state ──────────────────────────────────────────────────────────────
  const inFlight = useRef(false)
  const lastRefresh = useRef(0)
  const refreshTables = useCallback(() => {
    const now = Date.now()
    if (inFlight.current || now - lastRefresh.current < MIN_REFRESH_GAP_MS) return
    inFlight.current = true
    lastRefresh.current = now
    const sent = Date.now()
    api.tables().then((res) => {
      const got = Date.now()
      serverOffset.current = Date.parse(res.serverTime) - (sent + got) / 2
      setTables(res.tables)
      setLoadError(false)
    }).catch(() => setLoadError(true)).finally(() => { inFlight.current = false })
  }, [])

  useEffect(() => {
    refreshTables()
    const id = window.setInterval(refreshTables, CHECK_UPDATE_MS)
    return () => window.clearInterval(id)
  }, [refreshTables])

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now() + serverOffset.current), 200)
    return () => window.clearInterval(id)
  }, [])

  // ── Card order: drag a card by its header on desktop (the reference uses Sortable) ──
  const [order, setOrder] = useState<string[]>([])
  const products = useRef<HTMLDivElement>(null)
  const hasTables = tables !== null
  useEffect(() => {
    if (!hasTables || !products.current || document.documentElement.clientWidth <= 767) return
    const el = products.current
    const sortable = Sortable.create(el, {
      handle: '.gameheader',
      draggable: '.theproduct',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      // Picking a card up closes any open popover, as a click elsewhere would.
      onChoose: () => document.getElementById('app')?.click(),
      onEnd: (evt) => {
        const { oldIndex, newIndex, item } = evt
        if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return
        // Put the DOM back as React rendered it, then let React apply the move.
        el.removeChild(item)
        el.insertBefore(item, el.children[oldIndex] ?? null)
        setOrder((cur) => {
          const next = cur.slice()
          const [moved] = next.splice(oldIndex, 1)
          if (moved !== undefined) next.splice(newIndex, 0, moved)
          return next
        })
      },
    })
    return () => sortable.destroy()
  }, [hasTables])

  // ── Member ───────────────────────────────────────────────────────────────────
  const checkBalance = useCallback(() => {
    setChecking(true)
    api.wallet()
      .then((w) => setBalance(Number(w.balance)))
      .catch(() => {})
      .finally(() => window.setTimeout(() => setChecking(false), 500))
  }, [])

  const loadStakes = useCallback(() => {
    api.myBets(true, 1).then((bets) => {
      const next: Board['stakes'] = {}
      for (const b of bets) {
        const r = (next[b.roundId] ??= {})
        r[b.betType] = (r[b.betType] ?? 0) + Number(b.amount)
      }
      setStakes(next)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    api.me().then((m) => setUsername(m.member.username)).catch(() => {})
    checkBalance()
    loadStakes()
    const id = window.setInterval(checkBalance, CHECK_BALANCE_MS)
    return () => window.clearInterval(id)
  }, [checkBalance, loadStakes])

  // Back to a hidden tab: timers were throttled, so resync everything (the reference refreshPage).
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) return
      lastRefresh.current = 0
      refreshTables()
      loadStakes()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [refreshTables, loadStakes])

  const addStake = useCallback((roundId: string, betType: KenoBetType, amount: number) => {
    setStakes((s) => ({ ...s, [roundId]: { ...s[roundId], [betType]: (s[roundId]?.[betType] ?? 0) + amount } }))
  }, [])

  // ── Page chrome ──────────────────────────────────────────────────────────────
  const openWindow = useCallback((path: string) => {
    const url = `${path}${path.includes('?') ? '&' : '?'}l=${langId}`
    const w = 800, h = 600
    const left = window.screen.width / 2 - (w / 2 + 10)
    const top = window.screen.height / 2 - (h / 2 + 50)
    const win = window.open(url, 'atomwindow',
      `height=${h},width=${w},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,location=no,directories=no,status=no`)
    win?.focus()
  }, [langId])

  const fullscreen = useCallback((gameId: string) => {
    setFullscreenId((cur) => (cur === gameId ? null : gameId))
  }, [])

  // New tables go to the end; the dragged order of the rest is kept.
  const sortedTables = useMemo(() => {
    const list = tables ?? []
    const rank = (id: string) => { const i = order.indexOf(id); return i === -1 ? order.length : i }
    return list.slice().sort((a, b) => rank(a.gameId) - rank(b.gameId))
  }, [tables, order])
  useEffect(() => {
    if (!tables) return
    setOrder((cur) => [...cur.filter((id) => tables.some((x) => x.gameId === id)), ...tables.map((x) => x.gameId).filter((id) => !cur.includes(id))])
  }, [tables])

  const hiddenIds = useMemo(
    () => new Set(fullscreenId ? (tables ?? []).filter((x) => x.gameId !== fullscreenId).map((x) => x.gameId) : []),
    [fullscreenId, tables],
  )

  const board: Board = useMemo(() => ({
    displayMode,
    tier,
    balance,
    setBalance: (b: number) => setBalance(b),
    checkBalance,
    lastStake,
    setLastStake: (n: number) => setLastStake(n),
    stakes,
    addStake,
    refreshTables,
    serverNow: () => Date.now() + serverOffset.current,
    openWindow,
    playWin: () => { if (soundOn && winAudio) { winAudio.currentTime = 0; void winAudio.play().catch(() => {}) } },
    playLoss: () => { if (soundOn && lossAudio) { lossAudio.currentTime = 0; void lossAudio.play().catch(() => {}) } },
    fullscreen,
    hiddenIds,
    fullscreenId,
  }), [displayMode, tier, balance, checkBalance, lastStake, stakes, addStake, refreshTables, openWindow, soundOn, fullscreen, hiddenIds, fullscreenId])

  function logout() {
    session.clear()
    window.close()
    onDisconnected()
  }

  return (
    <LocaleContext.Provider value={{ langId, t }}>
      <BoardContext.Provider value={board}>
        <div id="app" className={['app', fullscreenId ? 'tablefullscreen' : ''].filter(Boolean).join(' ')}>
          <Header
            username={username}
            balance={balance}
            checking={checking}
            onCheckBalance={checkBalance}
            displayMode={displayMode}
            onDisplayMode={setDisplayMode}
            langId={langId}
            onLang={(n) => { setLangId(n); saveLang(n) }}
            soundOn={soundOn}
            onToggleSound={() => setSoundOn((s) => !s)}
            openWindow={openWindow}
            onLogout={logout}
          />
          <div id="products" className="products grid-x" ref={products}>
            {tables === null && !loadError && (
              <div className="loading" style={{ margin: '40px auto' }}><div className="loading-icon" /></div>
            )}
            {tables === null && loadError && (
              <div className="keno-load-error">
                Cannot reach the game server. <a onClick={refreshTables}>Retry</a>
              </div>
            )}
            {sortedTables.map((tb) => <GameLot key={tb.gameId} table={tb} nowMs={nowMs} />)}
          </div>
        </div>
      </BoardContext.Provider>
    </LocaleContext.Provider>
  )
}
